// app/components/FeaturedPrograms.tsx
'use client';
import { useState } from 'react';
import { Share2 } from 'lucide-react';
import Image from 'next/image';

interface FeaturedProgram {
  id: string;
  title: string;
  description: string;
  image: string;
  coverImage: string;
  category: string;
  targetAmount: number;
  collectedAmount: number;
  donorsCount: number;
  daysLeft: number;
  distributedAmount: number;
  beneficiaries: number;
  location: string;
  gradient: string;
  verified: boolean;
}

const FeaturedPrograms = () => {
  const [showAll, setShowAll] = useState(false);

  const programs: FeaturedProgram[] = [
    {
      id: '1',
      title: 'Aksi Nyata Bantu Yatim Piatu Untuk Anak yang Membutuhkan',
      description: 'Program pemberdayaan anak yatim piatu melalui pendidikan, pelatihan keterampilan, dan bantuan biaya hidup sehari-hari.',
      image: '👶',
      coverImage: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      category: 'Yatim & Dhuafa',
      targetAmount: 125000000,
      collectedAmount: 89750000,
      donorsCount: 2847,
      daysLeft: 45,
      distributedAmount: 76500000,
      beneficiaries: 340,
      location: 'Jakarta & Sekitarnya',
      gradient: 'from-blue-400 to-blue-600',
      verified: true
    },
    {
      id: '2',
      title: 'Bantu Seragam Sekolah Untuk Anak Yatim dan Dhuafa',
      description: 'Penyediaan seragam sekolah lengkap untuk anak-anak yatim dan dhuafa agar dapat mengikuti pendidikan dengan layak.',
      image: '👕',
      coverImage: 'https://images.unsplash.com/photo-1622737133809-d95047b9e673?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      category: 'Pendidikan',
      targetAmount: 50000000,
      collectedAmount: 37200000,
      donorsCount: 1456,
      daysLeft: 23,
      distributedAmount: 31800000,
      beneficiaries: 850,
      location: 'Bandung & Sekitarnya',
      gradient: 'from-emerald-400 to-emerald-600',
      verified: true
    },
    {
      id: '3',
      title: 'Program Beasiswa Anak Berprestasi dari Keluarga Kurang Mampu',
      description: 'Memberikan beasiswa pendidikan untuk anak-anak berprestasi dari keluarga kurang mampu hingga jenjang perguruan tinggi.',
      image: '🎓',
      coverImage: 'https://images.pexels.com/photos/20108061/pexels-photo-20108061.jpeg',
      category: 'Beasiswa',
      targetAmount: 200000000,
      collectedAmount: 145600000,
      donorsCount: 3921,
      daysLeft: 67,
      distributedAmount: 98400000,
      beneficiaries: 125,
      location: 'Seluruh Indonesia',
      gradient: 'from-purple-400 to-purple-600',
      verified: true
    },
    {
      id: '4',
      title: 'Bantuan Pangan Berkah untuk Keluarga Prasejahtera',
      description: 'Distribusi paket sembako dan makanan bergizi untuk keluarga prasejahtera di berbagai daerah.',
      image: '🍚',
      coverImage: 'https://images.unsplash.com/photo-1593113598332-cd288d649433?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      category: 'Pangan',
      targetAmount: 75000000,
      collectedAmount: 52300000,
      donorsCount: 1789,
      daysLeft: 31,
      distributedAmount: 48100000,
      beneficiaries: 1200,
      location: 'Jawa Tengah',
      gradient: 'from-orange-400 to-orange-600',
      verified: true
    },
    {
      id: '5',
      title: 'Renovasi Masjid dan Madrasah untuk Pendidikan Agama',
      description: 'Perbaikan dan renovasi masjid serta madrasah untuk meningkatkan kualitas pendidikan agama di pedesaan.',
      image: '🕌',
      coverImage: 'https://images.pexels.com/photos/2079666/pexels-photo-2079666.jpeg',
      category: 'Infrastruktur',
      targetAmount: 300000000,
      collectedAmount: 187500000,
      donorsCount: 2156,
      daysLeft: 89,
      distributedAmount: 125000000,
      beneficiaries: 450,
      location: 'Jawa Timur',
      gradient: 'from-cyan-400 to-cyan-600',
      verified: true
    },
    {
      id: '6',
      title: 'Klinik Kesehatan Gratis untuk Masyarakat Dhuafa',
      description: 'Pembangunan dan operasional klinik kesehatan gratis dengan layanan medis dasar untuk masyarakat kurang mampu.',
      image: '⚕️',
      coverImage: 'https://images.unsplash.com/photo-1516549655169-df83a0774514?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      category: 'Kesehatan',
      targetAmount: 150000000,
      collectedAmount: 98700000,
      donorsCount: 2634,
      daysLeft: 52,
      distributedAmount: 67200000,
      beneficiaries: 2100,
      location: 'Sumatera Utara',
      gradient: 'from-red-400 to-red-600',
      verified: true
    }
  ];

  const calculateProgress = (collected: number, target: number) => {
    return Math.min((collected / target) * 100, 100);
  };

  const displayedPrograms = showAll ? programs : programs.slice(0, 4);

  return (
    <div className="space-y-2 px-6">
      {/* Section Header */}
      <div className="flex items-center justify-between px-2">
        <div>
          <h3 className="font-bold text-lg text-gray-900 mb-2">
            Program Unggulan
          </h3>
          <p className="text-gray-600 text-sm">
            Program terpercaya dengan dampak nyata
          </p>
        </div>
        <button 
          onClick={() => setShowAll(!showAll)}
          className="flex items-center space-x-2 text-orange-600 font-semibold text-base hover:text-emerald-700 transition-colors"
        >
          <span className="text-[12px]">{showAll ? 'Tutup' : 'Lihat Semua'}</span>
          
        </button>
      </div>

      {/* Programs Grid - 2 Columns */}
      <div className="grid grid-cols-2 lg:grid-cols-2 gap-x-2 gap-y-4">
        {displayedPrograms.map((program) => {
          const progress = calculateProgress(program.collectedAmount, program.targetAmount);
          
          return (
            <div
              key={program.id}
              className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden group hover:shadow-2xl hover:-translate-y-1 transition-all duration-500"
            >
              {/* Cover Image */}
              <div className="relative h-48 overflow-hidden">
                <Image 
                  fill
                  src={program.coverImage} 
                  alt={program.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                {/* Floating Actions */}
                <div className="absolute top-1 right-2 flex space-x-2">                  
                  <button className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors shadow-lg">
                    <Share2 className="w-2 h-2 text-gray-600 hover:text-emerald-600 transition-colors" />
                  </button>
                </div>

                {/* Category Badge */}
                <div className="absolute top-1 left-1">
                  <span className="px-4 py-2 bg-white/95 backdrop-blur-sm text-gray-800 text-[8px] font-bold rounded-full shadow-lg">
                    {program.category}
                  </span>
                </div>

                {/* Verification Badge */}
                {program.verified && (
                  <div className="absolute bottom-1 left-1">
                    <div className="flex items-center space-x-2 px-3 py-1 bg-emerald-500/95 backdrop-blur-sm text-white text-[8px] font-bold rounded-full shadow-lg">
                      <span>✓</span>
                      <span>Terverifikasi</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Card Content */}
              <div className="p-2">
                {/* Title and Description */}
                <div className="mb-1">
                  <h4 className="font-bold text-gray-900 text-sm mb-1 line-clamp-2 group-hover:text-emerald-700 transition-colors leading-tight">
                    {program.title}
                  </h4>
                  <p className="text-gray-600 text-[10px] mb-1 line-clamp-2 leading-relaxed">
                    {program.description}
                  </p>                  
                </div>

                {/* Progress Section */}
                <div className="space-y-1">
                  {/* Main Progress */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-[12px] font-bold text-gray-700">Target Donasi</span>
                      <span className="text-sm font-bold text-emerald-600">{progress.toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 shadow-inner">
                      <div 
                        className={`bg-gradient-to-r ${program.gradient} h-3 rounded-full shadow-sm transition-all duration-700 ease-out`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Load More Button */}
      {!showAll && programs.length > 4 && (
        <div className="flex justify-center pt-8">
          <button 
            onClick={() => setShowAll(true)}
            className="px-12 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold rounded-2xl hover:from-emerald-600 hover:to-emerald-700 transition-all duration-300 hover:scale-105 transform shadow-xl hover:shadow-2xl"
          >
            Muat Lebih Banyak Program
          </button>
        </div>
      )}
    </div>
  );
};

export default FeaturedPrograms;